import React from 'react';

import { Button } from '@telus-uds/components-web';
import { getPdfUrl } from '../utils/utils';
import { orderStatus, quoteStatus } from '../utils/constant';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { useLocaleContext } from '../context/LocaleContext';
import FieldGuide from '../form/fields/common/fieldGuide';
import { MessageLabel, MessageMain } from './css';

const getLink = (id, label) => {
  const locale = useLocaleContext();
  return (
    <Button
      variant={{ text: true }}
      as="a"
      href={getPdfUrl(id, locale)}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'underline' }}
    >
      {label}
    </Button>
  );
};

const getButton = (onClick, label, isDisabled) => {
  // Determine variant based on action type
  // For table actions, use text variant for link-like appearance
  let variant = { text: true };
  let style = { textDecoration: 'underline', background: 'none', border: 'none', padding: 0 };
  
  // Check if label contains delete/cancel keywords - use danger semantic
  const labelLower = label?.toLowerCase() || '';
  if (labelLower.includes('delete') || labelLower.includes('cancel')) {
    variant = { priority: 'high', semantic: 'danger' };
    // For table context, keep text-like appearance but with danger color
    style = { textDecoration: 'underline', background: 'none', border: 'none', padding: 0, color: '#d32f2f' };
  }
  
  return (
    <Button 
      variant={variant} 
      onPress={onClick} 
      disabled={isDisabled}
      style={style}
    >
      {label}
    </Button>
  );
};

const getFinalStatus = (statusQuote, statusOrder) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    srtDashboard: { status }
  } = localizedStrings;

  let finalStatus;
  let statusId;
  if (
    statusQuote === quoteStatus.incomplete &&
    statusOrder === orderStatus.incomplete
  ) {
    finalStatus = status.incomplete;
    statusId = 'incomplete';
  } else if (
    statusQuote === quoteStatus.pendingApproval &&
    statusOrder === orderStatus.incomplete
  ) {
    finalStatus = status.pending;
    statusId = 'pending';
  } else if (
    statusQuote === quoteStatus.approved &&
    statusOrder === orderStatus.incomplete
  ) {
    finalStatus = status.approved;
    statusId = 'approved';
  } else if (statusOrder === orderStatus.pendingValidation) {
    finalStatus = status.pendingValidation;
    statusId = 'pendingValidation';
  } else if (statusOrder === orderStatus.waitingOnSales) {
    finalStatus = status.waitingOnSales;
    statusId = 'waitingOnSales';
  } else if (statusOrder === orderStatus.waitingOnCredit) {
    finalStatus = status.waitingOnCredit;
    statusId = 'waitingOnCredit';
  } else if (statusOrder === orderStatus.contractSent) {
    finalStatus = status.contractSent;
    statusId = 'contractSent';
  } else if (statusOrder === orderStatus.contractSigned) {
    finalStatus = status.contractSigned;
    statusId = 'contractSigned';
  } else if (statusOrder === orderStatus.contractExpired) {
    finalStatus = status.contractExpired;
    statusId = 'contractExpired';
  } else if (statusOrder === orderStatus.orderCanceled) {
    finalStatus = status.orderCanceled;
    statusId = 'orderCanceled';
  } else if (statusOrder === orderStatus.withConcierge) {
    finalStatus = status.withConcierge;
    statusId = 'withConcierge';
  } else if (statusOrder === orderStatus.validated) {
    finalStatus = status.validated;
    statusId = 'validated';
  } else if (statusOrder === orderStatus.implementationInProgress) {
    finalStatus = status.implementationInProgress;
    statusId = 'implementationInProgress';
  } else if (statusOrder === orderStatus.waitingOnCompletionCall) {
    finalStatus = status.waitingOnCompletionCall;
    statusId = 'waitingOnCompletionCall';
  } else if (statusOrder === orderStatus.orderInstalled) {
    finalStatus = status.orderInstalled;
    statusId = 'orderInstalled';
  } else if (statusOrder === quoteStatus.approved) {
    finalStatus = status.approved;
    statusId = 'quoteApproved';
  } else if (statusOrder === quoteStatus.incomplete) {
    finalStatus = status.incomplete;
    statusId = 'orderIncomplete';
  } else if (statusOrder === orderStatus.orderSubmitted) {
    finalStatus = status.orderSubmitted;
    statusId = 'orderSubmitted';
  }
  return { finalStatus, statusId };
};

const getStatus = ({ status: order, quoteStatus: quote }) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    srtDashboard: { statusToolTipMessage }
  } = localizedStrings;

  const { finalStatus, statusId } = getFinalStatus(quote, order);

  const getToolTip = () => {
    const message = statusToolTipMessage[statusId];
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {finalStatus}
        <FieldGuide tooltip={message} />
      </div>
    );
  };

  return { statusId, finalStatus, statusToolTip: getToolTip() };
};

const getMessage = data => {
  const { isContractResend, isOrderReturned, isLockedBy } = data;
  const localizedStrings = useLocalizedStringsContext();
  const {
    sovTableMessages: { resendContract, returnOrder }
  } = localizedStrings.ORDERS;
  return (
    <MessageMain>
      {isContractResend && <MessageLabel>{resendContract}</MessageLabel>}
      {isOrderReturned && <MessageLabel>{returnOrder}</MessageLabel>}
      {isLockedBy && (
        <p
          style={{ fontSize: '12px', fontWeight: '500' }}
        >{`This order is being currently worked on by ${isLockedBy}`}</p>
      )}
    </MessageMain>
  );
};

export { getLink, getButton, getStatus, getMessage };
